import { useState } from 'react';
import {
  Alert, Box, Button, Chip, FormControlLabel, IconButton, Paper, Radio, RadioGroup, Stack, TextField, Typography,
} from '@mui/material';
import { Add, Close, ContentCopy, DeleteOutline, ImageOutlined } from '@mui/icons-material';
import { ColorsConstants } from '../../../cssUtils/constants';
import { ratioError, readMediaSize } from './api';
import {
  AD_FORMATS, HEADLINE_MAX, MAX_ADS, MAX_CAROUSEL_CARDS, MIN_ADS, MIN_CAROUSEL_CARDS,
  PRIMARY_TEXT_RECOMMENDED, emptyAd, emptyCard, newKey,
} from './constants';
import { adErrors } from './validation';
import { FieldLabel, Note } from './ui';

const radioSx = { color: ColorsConstants.primaryBlue100, '&.Mui-checked': { color: ColorsConstants.primaryBlue100 } };

const counter = (len, limit) => (
  <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', color: len > limit ? '#B45309' : ColorsConstants.primaryGray50 }}>
    {len} / {limit}{len > limit ? ' – longer text may be cut off' : ''}
  </Typography>
);

const UploadBox = ({ accept, label, onPick }) => (
  <Box
    component="label"
    sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2.5, cursor: 'pointer',
      border: `2px dashed ${ColorsConstants.primaryGray20}`, borderRadius: 2, backgroundColor: '#F8FAFC',
      '&:hover': { borderColor: ColorsConstants.primaryBlue100, backgroundColor: ColorsConstants.primaryBlue60 },
    }}
  >
    <input type="file" hidden accept={accept} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onPick(f); }} />
    <ImageOutlined sx={{ fontSize: 30, color: ColorsConstants.primaryGray40, mb: 0.5 }} />
    <Typography variant="body2" fontWeight={700} color={ColorsConstants.primaryGray60}>{label}</Typography>
    <Note sx={{ textAlign: 'center', mt: 0.5 }}>1:1, 4:5 or 9:16 · checked when you pick the file</Note>
  </Box>
);

const PickedFile = ({ name, ratio, width, height, previewUrl, isVideo, onRemove }) => (
  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ p: 1.25, backgroundColor: ColorsConstants.primaryBlue60, borderRadius: 2 }}>
    {previewUrl && !isVideo
      ? <Box component="img" src={previewUrl} alt="" sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1 }} />
      : <ImageOutlined sx={{ fontSize: 32, color: ColorsConstants.primaryBlue100 }} />}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" fontWeight={700} noWrap>{name || 'Uploaded file'}</Typography>
      {ratio && <Typography variant="caption" color="#166534">{ratio}{width ? ` · ${width}×${height}` : ''}</Typography>}
    </Box>
    <IconButton size="small" aria-label="Remove file" onClick={onRemove}><Close fontSize="small" sx={{ color: '#EF4444' }} /></IconButton>
  </Stack>
);

// Editor for one ad. Also used by the Manage screen (add ad, edit & resubmit).
export const AdEditor = ({ ad, onChange }) => {
  const [pickError, setPickError] = useState('');

  const readFile = async (file) => {
    setPickError('');
    try {
      const size = await readMediaSize(file);
      if (!size.ratio) {
        URL.revokeObjectURL(size.url);
        setPickError(ratioError(size.width, size.height));
        return null;
      }
      return { file, fileName: file.name, previewUrl: size.url, width: size.width, height: size.height, ratio: size.ratio };
    } catch (e) {
      setPickError(e.message);
      return null;
    }
  };

  const pickSingle = async (file) => {
    const picked = await readFile(file);
    if (picked) onChange({ ...picked, mediaAssetId: null });
  };

  const pickCard = async (idx, file) => {
    const picked = await readFile(file);
    if (picked) onChange({ cards: ad.cards.map((c, i) => (i === idx ? { ...c, ...picked } : c)), mediaAssetId: null });
  };

  const setCard = (idx, patch) => onChange({ cards: ad.cards.map((c, i) => (i === idx ? { ...c, ...patch } : c)), mediaAssetId: null });

  const hasUploaded = !!ad.mediaAssetId && !ad.file && ad.format !== 'CAROUSEL';

  return (
    <Stack spacing={2}>
      <Box>
        <FieldLabel hint="(only you see this)">Ad name</FieldLabel>
        <TextField fullWidth size="small" value={ad.name} onChange={(e) => onChange({ name: e.target.value })} />
      </Box>

      <Box>
        <FieldLabel>Format</FieldLabel>
        <RadioGroup row value={ad.format} onChange={(e) => { setPickError(''); onChange({ format: e.target.value, file: null, previewUrl: '', ratio: '', mediaAssetId: null }); }}>
          {AD_FORMATS.map((f) => (
            <FormControlLabel key={f.value} value={f.value} control={<Radio size="small" sx={radioSx} />} label={<Typography variant="body2">{f.label}</Typography>} />
          ))}
        </RadioGroup>
      </Box>

      {pickError && <Alert severity="error" onClose={() => setPickError('')}>{pickError}</Alert>}

      {ad.format !== 'CAROUSEL' ? (
        ad.file || hasUploaded ? (
          <PickedFile
            name={ad.fileName} ratio={ad.ratio} width={ad.width} height={ad.height} previewUrl={ad.previewUrl}
            isVideo={ad.format === 'VIDEO'}
            onRemove={() => onChange({ file: null, fileName: '', previewUrl: '', ratio: '', mediaAssetId: null })}
          />
        ) : (
          <UploadBox
            accept={ad.format === 'VIDEO' ? 'video/*' : 'image/jpeg,image/png'}
            label={ad.format === 'VIDEO' ? 'Upload video' : 'Upload image'}
            onPick={pickSingle}
          />
        )
      ) : (
        <Stack spacing={1.5}>
          {ad.mediaAssetId && ad.cards.every((c) => !c.file) && (
            <Alert severity="info">These cards were uploaded earlier. Replace a card's image to change them.</Alert>
          )}
          {ad.cards.map((card, idx) => (
            <Paper key={card.key} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight={700}>Card {idx + 1}</Typography>
                {ad.cards.length > MIN_CAROUSEL_CARDS && (
                  <IconButton size="small" aria-label={`Remove card ${idx + 1}`} onClick={() => onChange({ cards: ad.cards.filter((_, i) => i !== idx), mediaAssetId: null })}>
                    <Close fontSize="small" sx={{ color: '#EF4444' }} />
                  </IconButton>
                )}
              </Stack>
              <Box sx={{ mb: 1 }}>
                {card.file
                  ? <PickedFile name={card.fileName} ratio={card.ratio} width={card.width} height={card.height} previewUrl={card.previewUrl} onRemove={() => setCard(idx, { file: null, previewUrl: '', ratio: '' })} />
                  : <UploadBox accept="image/jpeg,image/png" label="Upload image" onPick={(f) => pickCard(idx, f)} />}
              </Box>
              <TextField fullWidth size="small" placeholder="Card headline" value={card.headline} sx={{ mb: 0.5 }}
                onChange={(e) => setCard(idx, { headline: e.target.value })} />
              {counter(card.headline.length, HEADLINE_MAX)}
              <TextField fullWidth size="small" placeholder="Card description (optional)" value={card.description}
                onChange={(e) => setCard(idx, { description: e.target.value })} />
            </Paper>
          ))}
          {ad.cards.length < MAX_CAROUSEL_CARDS && (
            <Button size="small" startIcon={<Add />} sx={{ alignSelf: 'flex-start' }}
              onClick={() => onChange({ cards: [...ad.cards, emptyCard()], mediaAssetId: null })}>
              Add card
            </Button>
          )}
        </Stack>
      )}

      <Box>
        <FieldLabel hint="(the main text above the image)">Primary text</FieldLabel>
        <TextField fullWidth multiline minRows={3} size="small" value={ad.primaryText} placeholder="Your offer in one or two lines"
          onChange={(e) => onChange({ primaryText: e.target.value })} />
        {counter(ad.primaryText.length, PRIMARY_TEXT_RECOMMENDED)}
      </Box>

      <Box>
        <FieldLabel>Headline</FieldLabel>
        <TextField fullWidth size="small" value={ad.headline} placeholder="Short headline"
          onChange={(e) => onChange({ headline: e.target.value.slice(0, HEADLINE_MAX) })} />
        {counter(ad.headline.length, HEADLINE_MAX)}
      </Box>

      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="caption" fontWeight={700}>Button</Typography>
        <Chip size="small" label="Send message" sx={{ backgroundColor: '#15803D', color: 'white', fontWeight: 700 }} />
        <Note>Fixed for WhatsApp ads</Note>
      </Stack>
    </Stack>
  );
};

const AdsSection = ({ ads, onChange, showErrors }) => {
  const [activeKey, setActiveKey] = useState(ads[0]?.key);
  const active = ads.find((a) => a.key === activeKey) || ads[0];

  const update = (key, patch) => onChange(ads.map((a) => (a.key === key ? { ...a, ...patch } : a)));
  const add = () => { const ad = emptyAd(ads.length + 1); onChange([...ads, ad]); setActiveKey(ad.key); };
  const duplicate = () => {
    const copy = { ...active, key: newKey(), name: `${active.name} (copy)`, cards: active.cards.map((c) => ({ ...c, key: newKey() })) };
    onChange([...ads, copy]);
    setActiveKey(copy.key);
  };
  const remove = () => {
    const rest = ads.filter((a) => a.key !== active.key);
    onChange(rest);
    setActiveKey(rest[0]?.key);
  };

  return (
    <Box>
      <Note sx={{ mb: 1.5 }}>Add {MIN_ADS}–{MAX_ADS} ads with different images or videos. Meta shows the best performer more often.</Note>
      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ pb: 1.5, mb: 2, borderBottom: `1px solid ${ColorsConstants.primaryGray10}` }}>
        {ads.map((a, i) => {
          const issues = showErrors ? adErrors(a, `Ad ${i + 1}`).length : 0;
          const selected = a.key === active?.key;
          return (
            <Button
              key={a.key} onClick={() => setActiveKey(a.key)} size="small"
              sx={{
                textTransform: 'none', fontWeight: selected ? 700 : 500, borderRadius: 2, px: 1.5,
                border: `${selected ? 2 : 1}px solid ${issues ? '#B91C1C' : selected ? ColorsConstants.primaryBlue100 : ColorsConstants.primaryGray20}`,
                backgroundColor: issues ? '#FEF2F2' : selected ? ColorsConstants.primaryBlue60 : 'white',
                color: issues ? '#991B1B' : ColorsConstants.primaryGray60,
              }}
            >
              Ad {i + 1} · {AD_FORMATS.find((f) => f.value === a.format)?.label}{issues ? ` – ${issues} to fix` : ''}
            </Button>
          );
        })}
        {ads.length < MAX_ADS && (
          <Button size="small" startIcon={<Add />} onClick={add} sx={{ textTransform: 'none', border: `1px dashed ${ColorsConstants.primaryBlue100}`, borderRadius: 2 }}>
            Add ad ({ads.length} of {MAX_ADS})
          </Button>
        )}
        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
          {ads.length < MAX_ADS && (
            <Button size="small" startIcon={<ContentCopy fontSize="small" />} onClick={duplicate} sx={{ textTransform: 'none' }}>Duplicate</Button>
          )}
          {ads.length > MIN_ADS && (
            <Button size="small" color="error" startIcon={<DeleteOutline fontSize="small" />} onClick={remove} sx={{ textTransform: 'none' }}>Remove</Button>
          )}
        </Box>
      </Stack>
      {active && <AdEditor key={active.key} ad={active} onChange={(patch) => update(active.key, patch)} />}
    </Box>
  );
};

export default AdsSection;
